import { Injectable } from '@angular/core';

import { HelperLinesService } from '../../helper-lines.service';
import { SizeHelperService } from '../../size-helper.service';
import { Edges } from '..';
import { StageService } from '../../stage.service';

declare const THREE: any;

@Injectable()
export class RailingCalculationHelperService {
  stairLength: number;

  constructor(
    private sizeHelper: SizeHelperService,
    private stage: StageService,
    private helperLinesService: HelperLinesService
  ) {

    this.stairLength = this.sizeHelper.INCHToCM(9);
  }

  // refactor params
  getPositionsForIntermediatePosts(floor, maximumWidth, intermediatePost, cornerPost,
    cornerPost45degree,
    cornerPost135degree
  ) {
    const deckHeight = floor.getWorldPosition(new THREE.Vector3()).y;

    const intermediatePoints = [];
    const counts = [];
    const widths = [];

    let edges = [];

    const edgesAndTerminationPostPositions = this.getPositionsForTerminationPosts(floor, intermediatePost);
    edges = edgesAndTerminationPostPositions.newEdges;
    const terminationPostsPositions = edgesAndTerminationPostPositions.terminationPostsPositions;

    // Rail-length modifier / dock anchors: per-edge railStart/railStop offsets
    // and noRail flags stored on floor points shorten or remove the run along
    // that edge. The full ring is kept for neighbour lookups below: taking
    // neighbours from the filtered list would pair non-adjacent edges (or an
    // edge with itself), producing zero-length vectors and NaN angles/offsets.
    const fullRing = edges;
    edges = this.applyRailRunLimits(floor, edges, terminationPostsPositions, intermediatePost);

    for (let i = 0; i < edges.length; i++) {

      const ringIndex = fullRing.indexOf(edges[i]);
      const ring = ringIndex >= 0 ? fullRing : edges;
      const rIdx = ringIndex >= 0 ? ringIndex : i;

      let A = ring[rIdx === 0 ? ring.length - 1 : rIdx - 1].firstPoint,
        B: any = edges[i].firstPoint,
        C: any = edges[i].secondPoint,
        D = ring[(rIdx + 1) % ring.length].secondPoint;

      let BA = new THREE.Vector3(A.x - B.x, 0, A.y - B.y),
        BC = new THREE.Vector3(C.x - B.x, 0, C.y - B.y),
        CB = new THREE.Vector3(B.x - C.x, 0, B.y - C.y),
        CD = new THREE.Vector3(D.x - C.x, 0, D.y - C.y);

      const norm = new THREE.Vector3(0, 0, 1);
      let angle = norm.angleTo(BC); // - Math.PI / 2;
      if (BC.x < 0) {
        // norm.negate();
        angle = -BC.angleTo(norm);
      }

      angle -= 3 * Math.PI / 2;

      let leftOffset, rightOffset;

      const leftOffsetVector = BC.clone(),
        rightOffsetVector = CB.clone();

      const postSize = new THREE.Box3().setFromObject(intermediatePost).getSize(new THREE.Vector3());
      const edgeOffset = +intermediatePost.userData.entity.offset;
      leftOffset = Math.abs(this.sizeHelper.INCHToCM(2.5) + +intermediatePost.userData.entity.offset);
      rightOffset = Math.abs(this.sizeHelper.INCHToCM(2.5) + +intermediatePost.userData.entity.offset);

      let leftNot90 = false;
      let rightNot90 = false;
      let left45 = false;
      let right45 = false;
      let left135 = false;
      let right135 = false;

      if ((BA.angleTo(BC) >= 3 * Math.PI / 4 - 0.005) && (BA.angleTo(BC) <= 3 * Math.PI / 4 + 0.005)) {
        if ((BA.x * BC.z - BA.z * BC.x < 0 && cornerPost45degree)) {
          left45 = true;
        }
        if ((BA.x * BC.z - BA.z * BC.x > 0 && cornerPost135degree)) {
          left135 = true;
        }
      }

      if ((CB.angleTo(CD) >= 3 * Math.PI / 4 - 0.005) && (CB.angleTo(CD) <= 3 * Math.PI / 4 + 0.005)) {
        if ((CB.x * CD.z - CB.z * CD.x < 0 && cornerPost45degree)) {
          right45 = true;
        }
        if ((CB.x * CD.z - CB.z * CD.x > 0 && cornerPost135degree)) {
          right135 = true;
        }
      }

      if (!((BA.angleTo(BC) % (Math.PI / 2) < 0.005) || (BA.angleTo(BC) % (Math.PI / 2) > Math.PI / 2 - 0.005))) {
        const angleBetweenEdges = BA.angleTo(BC);
        leftNot90 = true;

        if (+intermediatePost.userData.entity.offset > 0) {
          if (BA.x * BC.z - BA.z * BC.x > 0) {
            leftOffsetVector.negate();
          } else {
            leftOffset = Math.abs(((postSize.x + edgeOffset) / Math.tan(angleBetweenEdges / 2)) + postSize.x / 2);
          }
        } else {
          if (BA.x * BC.z - BA.z * BC.x < 0) {
            leftOffsetVector.negate();
          } else {
            leftOffset = Math.abs(((postSize.x - edgeOffset) / Math.tan(angleBetweenEdges / 2)) + postSize.x / 2);
          }
        }
      }

      if (!((CB.angleTo(CD) % (Math.PI / 2) < 0.005) || (CB.angleTo(CD) % (Math.PI / 2) > Math.PI / 2 - 0.005))) {
        const angleBetweenEdges = CB.angleTo(CD);
        rightNot90 = true;

        if (+intermediatePost.userData.entity.offset > 0) {
          if (CB.x * CD.z - CB.z * CD.x > 0) {
            rightOffsetVector.negate();
          } else {
            rightOffset = Math.abs(((postSize.x + edgeOffset) / Math.tan(angleBetweenEdges / 2)) + postSize.x / 2);
          }
        } else {
          if (CB.x * CD.z - CB.z * CD.x < 0) {
            rightOffsetVector.negate();
          } else {
            rightOffset = Math.abs(((postSize.x - edgeOffset) / Math.tan(angleBetweenEdges / 2)) + postSize.x / 2);
          }
        }
      }

      if (+intermediatePost.userData.entity.offset > 0) {
        if (BA.x * BC.z - BA.z * BC.x > 0) {
          leftOffsetVector.negate();
        }

        if (CB.x * CD.z - CB.z * CD.x > 0) {
          rightOffsetVector.negate();
        }
      } else {
        if (BA.x * BC.z - BA.z * BC.x < 0) {
          leftOffsetVector.negate();
        }

        if (CB.x * CD.z - CB.z * CD.x < 0) {
          rightOffsetVector.negate();
        }
      }

      const wallDirection = new THREE.Vector3(C.x - B.x, 0, C.y - B.y);

      wallDirection.normalize();
      const offsetVector = new THREE.Vector3(- wallDirection.z, 0, wallDirection.x);

      let offset = this.sizeHelper.INCHToCM(2.5);
      if (intermediatePost.userData.entity) {
        offset += +intermediatePost.userData.entity.offset;
      }

      if (offset < 0) {
        offset = -offset;
        offsetVector.negate();
      }

      offsetVector.setLength(offset);

      /////////////////////////////////////////////////////////////////////////////////////////////////////////

      if (left45 || left135) {
        leftOffset = 0;
      }
      if (right45 || right135) {
        rightOffset = 0;
      }
      leftOffsetVector.setLength(leftOffset);
      rightOffsetVector.setLength(rightOffset);

      if (edges[i].isFirstPointTerminationPost) {
        B = new THREE.Vector3(edges[i].firstPostPosition.x, 0, edges[i].firstPostPosition.z);
      } else {
        B = new THREE.Vector3(B.x, 0, B.y);
        B.add(leftOffsetVector);
        B.add(offsetVector);
      }
      if (edges[i].isSecondPointTerminationPost) {
        C = new THREE.Vector3(edges[i].secondPostPosition.x, 0, edges[i].secondPostPosition.z);
      } else {
        C = new THREE.Vector3(C.x, 0, C.y);
        C.add(rightOffsetVector);
        C.add(offsetVector);
      }
      /////////////////////////////////////////////////////////////////////////////////////////////

      let BtoC = new THREE.Vector3(C.x - B.x, 0, C.z - B.z);
      BtoC.setLength(cornerPost.userData.entity.width / 2);
      let CtoB = new THREE.Vector3(B.x - C.x, 0, B.z - C.z);
      CtoB.setLength(cornerPost.userData.entity.width / 2);

      if (cornerPost.userData.entity.cornerType === 'Double') {
        if (!edges[i].isFirstPointTerminationPost && !left45 && !left135 && !leftNot90) {
          B.add(BtoC);
        }

        if (!edges[i].isSecondPointTerminationPost && !right45 && !right135 && !rightNot90) {
          C.add(CtoB);
        }
      }

      /////////////////////////////////////////////////////////////////////////////////////////////

      const length = B.distanceTo(C);

      let width, count;

      if (maximumWidth > length) {
        width = length;
        count = 0;
      } else {
        for (let j = 1; maximumWidth * (j - 1) < length; j++) {
          width = length / j;
          count = j - 1;
        }
      }
      widths.push(width);
      counts.push(count);

      ////////////////////////////////////////////////////////////////////////////////////////////

      wallDirection.multiplyScalar(width);

      const points = [];
      const firstPoint = new THREE.Vector3(B.x, deckHeight, B.z);

      //////////////////////////////////////////////////////////////////////////////////////////

      for (let j = 0; j < count + 2; j++) {

        const p = firstPoint.clone();
        firstPoint.add(wallDirection);

        // p.add(offsetVector);
        p.rotation = angle;
        // p.X = X;
        p.rotationVector = BC.clone();
        p.leftOffsetVector = leftOffsetVector;
        p.rightOffsetVector = rightOffsetVector;

        if (j === 0 || j === count + 1) {
          p.corner = true;
        }

        points.push(p);
      }

      if (edges[i].isFirstPointTerminationPost) {
        points[0].isTerminationPost = true;
        if (edges[i].firstPostPosition.isStairPost) {
          points[0].isStairPost = true;
        }
        if (edges[i].firstPostPosition.isOutPoint) {
          points[0].isOutPoint = true;
        }
      }

      if (edges[i].isSecondPointTerminationPost) {
        points[count + 1].isTerminationPost = true;
        if (edges[i].secondPostPosition.isStairPost) {
          points[count + 1].isStairPost = true;
        }
        if (edges[i].secondPostPosition.isOutPoint) {
          points[count + 1].isOutPoint = true;
        }
      }

      if (leftNot90) {
        points[0].not90 = true;
      }

      if (rightNot90) {
        points[count + 1].not90 = true;
      }

      if (left45) {
        points[0].Is45 = true;
      }

      if (right45) {
        points[count + 1].Is45 = true;
      }

      if (left135) {
        points[0].Is135 = true;
      }

      if (right135) {
        points[count + 1].Is135 = true;
      }

      points[0].floorPoint = edges[i].firstPoint;

      points[count + 1].floorPoint = edges[i].secondPoint;

      if (cornerPost.userData.entity.cornerType === 'Double') {
        if (!edges[i].isFirstPointTerminationPost && !left45 && !leftNot90) {
          points[0].add(CtoB);
        }

        if (!edges[i].isSecondPointTerminationPost && !right45 && !rightNot90) {
          points[count + 1].add(BtoC);
        }
      }

      intermediatePoints.push(points);
    }
    return { intermediatePoints, terminationPostsPositions };
  }

  // Applies per-edge run limits (railStart/railStop, in cm from the edge's first
  // point) written by the RailLengthModifierService, and the noRail flag set by
  // dock templates / DockRailAnchorService (a dock side has no railing until
  // its anchor is clicked). A limited edge gets termination posts at the run
  // boundaries; when the remaining run is shorter than one DockRail section
  // (60") the edge is skipped entirely.
  private applyRailRunLimits(floor, edges, terminationPostsPositions, intermediatePost) {
    const points = floor.userData.points;
    const hasLimits = points && points.some(p => p.railStart > 0 || p.railStop > 0);
    const hasResets = points && points.some(p => p.runLimitNotCorner);
    const hasNoRail = points && points.some(p => p.noRail);
    if (!hasLimits && !hasResets && !hasNoRail) {
      return edges;
    }

    const deckHeight = floor.getWorldPosition(new THREE.Vector3()).y;
    const minRun = this.sizeHelper.INCHToCM(60);
    const resultEdges = [];

    let inset = this.sizeHelper.INCHToCM(2.5);
    if (intermediatePost.userData.entity) {
      inset += +intermediatePost.userData.entity.offset;
    }

    const isEdgeRemoved = point => {
      if (!point) {
        return false;
      }
      if (point.noRail) {
        return true;
      }
      if (!point.railStart && !point.railStop) {
        return false;
      }
      const next = points[(points.indexOf(point) + 1) % points.length];
      const length = Math.sqrt(
        Math.pow(next.x - point.x, 2) + Math.pow(next.y - point.y, 2)
      );
      return length - (point.railStart || 0) - (point.railStop || 0) < minRun;
    };

    for (let i = 0; i < edges.length; i++) {
      const edge = edges[i];
      const point = edge.firstPoint;
      if (point && point.noRail) {
        // dock side whose anchor hasn't been clicked yet
        continue;
      }
      const railStart = point && point.railStart ? +point.railStart : 0;
      const railStop = point && point.railStop ? +point.railStop : 0;

      if (railStart <= 0 && railStop <= 0) {
        resultEdges.push(edge);
        continue;
      }

      const dir = new THREE.Vector3(
        edge.secondPoint.x - edge.firstPoint.x,
        0,
        edge.secondPoint.y - edge.firstPoint.y
      );
      const length = dir.length();
      dir.normalize();

      if (length - railStart - railStop < minRun) {
        // run too short — no railing on this edge; neighbouring runs keep their corners
        continue;
      }

      let offset = inset;
      const offsetVector = new THREE.Vector3(-dir.z, 0, dir.x);
      if (offset < 0) {
        offset = -offset;
        offsetVector.negate();
      }
      offsetVector.setLength(offset);

      const makeRunPost = distanceAlongEdge => {
        const p = new THREE.Vector3(
          edge.firstPoint.x + dir.x * distanceAlongEdge,
          deckHeight,
          edge.firstPoint.y + dir.z * distanceAlongEdge
        );
        p.add(offsetVector);
        p.vector = dir.clone();
        p.direction = dir.clone();
        return p;
      };

      if (railStart > 0 && !edge.isFirstPointTerminationPost) {
        const lPoint = makeRunPost(railStart);
        edge.isFirstPointTerminationPost = true;
        edge.firstPostPosition = lPoint;
        terminationPostsPositions.push(lPoint);
      }

      if (railStop > 0 && !edge.isSecondPointTerminationPost) {
        const rPoint = makeRunPost(length - railStop);
        edge.isSecondPointTerminationPost = true;
        edge.secondPostPosition = rPoint;
        terminationPostsPositions.push(rPoint);
      }

      resultEdges.push(edge);
    }

    // Corner posts: suppress a corner only when BOTH adjacent runs pulled back
    // from it; a single-sided pull keeps the corner post as the other run's end.
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const prevPoint = points[(i - 1 + points.length) % points.length];
      const startPulled = (point.railStart || 0) > 0 || isEdgeRemoved(point);
      const prevStopPulled = (prevPoint.railStop || 0) > 0 || isEdgeRemoved(prevPoint);

      if (startPulled && prevStopPulled) {
        point.notCorner = true;
        point.runLimitNotCorner = true;
      } else if (point.runLimitNotCorner) {
        // limit removed by the user — restore the corner we suppressed earlier
        point.notCorner = false;
        point.runLimitNotCorner = false;
      }
    }

    return resultEdges;
  }

  private getAdditionalDistance(point) {
    return point.additionalDistance ? point.additionalDistance : 0;
  }

  private isTiersSuperstructureType(point) {
    return point.superstructureType === 'tier1StepDown' ||
      point.superstructureType === 'tier2StepDown' ||
      point.superstructureType === 'tier3StepDown' ||
      point.superstructureType === 'tier1StepUp' ||
      point.superstructureType === 'tier2StepUp' ||
      point.superstructureType === 'tier3StepUp';
  }

  private toPrecision(num: number) {
    return parseFloat(Number.parseFloat(num.toPrecision()).toPrecision(12));
  }

  getPositionsForTerminationPosts(floor, intermediatePost) {
    const deckHeight = floor.getWorldPosition(new THREE.Vector3()).y;
    const terminationPostsPositions = [];
    const edges = [...floor.userData.edges];

    const newEdges = [];
    const Offset = this.sizeHelper.INCHToCM(2.5) + +intermediatePost.userData.entity.offset;

    const isWood = (intermediatePost.userData.entity.railingType[0] === "Wood" && Offset < 0);

    for (let i = 0; i < floor.userData.points.length; i++) {
      const point = floor.userData.points[i];
      point.terminationPosts = null;
      const secondPoint = floor.userData.points[(i + 1) % floor.userData.points.length];
      point.notCorner = false;

      // add tier steps info to parent tier
      if (this.isTiersSuperstructureType(point) && !point.parent) {
        const childTier = this.stage.getCompoundFloor().children.find(tier => tier.userData.customId === point.tierId);
        const childTierPoint = childTier.userData.points[childTier.userData.points.length - 1];
        const thisPointChildPoint = new THREE.Vector3(childTierPoint.x - point.x, 0, childTierPoint.y - point.y);
        const thisFirstPointThisSecondPoint = new THREE.Vector3(secondPoint.x - point.x, 0, secondPoint.y - point.y);
        const thisSecondPointChildFirstPoint = new THREE.Vector3(childTierPoint.x - secondPoint.x, 0, childTierPoint.y - secondPoint.y);
        const thisSecondPointThisFirstPoint = new THREE.Vector3(point.x - secondPoint.x, 0, point.y - secondPoint.y);

        point.width = childTierPoint.width;
        point.distance = (
          thisPointChildPoint.x *
          thisFirstPointThisSecondPoint.x +
          thisPointChildPoint.z *
          thisFirstPointThisSecondPoint.z
        ) / thisFirstPointThisSecondPoint.length() - childTierPoint.distance;

        const projectThisSecondPointOnChildFirstPoint = (
          thisSecondPointChildFirstPoint.x *
          thisSecondPointThisFirstPoint.x +
          thisSecondPointChildFirstPoint.z *
          thisSecondPointThisFirstPoint.z
        ) / thisSecondPointThisFirstPoint.length();

        if (projectThisSecondPointOnChildFirstPoint < 0) {
          point.distance -= -projectThisSecondPointOnChildFirstPoint;
        }
      }
    }

    for (let i = 0; i < floor.userData.points.length; i++) {
      const childTierPoint = floor.userData.points[i];
      const childSecondPoint = floor.userData.points[0];

      if (this.isTiersSuperstructureType(childTierPoint) && childTierPoint.parent) {
        const parentTier = this.stage.getCompoundFloor().children.find(tier => tier.userData.customId === floor.userData.parentCustomId);
        const parentFirstPointIndex = parentTier.userData.points.findIndex(point => point.childCustomId === floor.userData.customId);
        const firstPoint = parentTier.userData.points[parentFirstPointIndex];
        const secondPoint = parentTier.userData.points[(parentFirstPointIndex + 1) % parentTier.userData.points.length];

        const childFirstPointThisSecondPoint = new THREE.Vector3(
          secondPoint.x - childTierPoint.x,
          0,
          secondPoint.y - childTierPoint.y
        );
        const childFirstPointChildSecondPoint = new THREE.Vector3(
          childSecondPoint.x - childTierPoint.x,
          0,
          childSecondPoint.y - childTierPoint.y
        );
        const projectChildFirstPointOnChildSecondPoint =
          (childFirstPointThisSecondPoint.x *
            childFirstPointChildSecondPoint.x +
            childFirstPointThisSecondPoint.z *
            childFirstPointChildSecondPoint.z) / childFirstPointChildSecondPoint.length();

        if (projectChildFirstPointOnChildSecondPoint > 0) {
          childTierPoint.additionalDistance = projectChildFirstPointOnChildSecondPoint;
        } else {
          childTierPoint.additionalDistance = 0;
        }
      }
    }

    for (let i = 0; i < floor.userData.points.length; i++) {
      const previousPoint = floor.userData.points[(i > 0) ? (i - 1) : (floor.userData.points.length - 1)];
      const nextPoint = floor.userData.points[(i < floor.userData.points.length - 1) ? (i + 1) : 0];
      const previousEdge = edges[(i > 0) ? (i - 1) : (floor.userData.points.length - 1)];
      const nextEdge = edges[(i < floor.userData.points.length - 1) ? (i + 1) : 0];

      let extraOffsetL = 0;
      let extraOffsetR = 0;
      let leftExtraOffset = 0;
      let rightExtraOffset = 0;
      if (previousPoint.superstructureType && previousPoint.superstructureType === "wall") {
        if (floor.userData.points[i].superstructureType && floor.userData.points[i].superstructureType === "wall") {
          leftExtraOffset = 21;
          extraOffsetL = 21;
        }
      }
      if (nextPoint.superstructureType && nextPoint.superstructureType === "wall") {
        if (floor.userData.points[i].superstructureType && floor.userData.points[i].superstructureType === "wall") {
          extraOffsetR = 21;
          rightExtraOffset = 21;
        }
      }

      let needRight = true;
      let needLeft = true;
      const prev = new THREE.Vector3(previousPoint.x, 0, previousPoint.y);
      const first = new THREE.Vector3(floor.userData.points[i].x, 0, floor.userData.points[i].y);
      const second = new THREE.Vector3(nextPoint.x, 0, nextPoint.y);

      const prevEdgeLength = prev.distanceTo(first);
      const edgeLength = first.distanceTo(second);

      if (previousPoint.superstructureType === "wall") {
        if (previousPoint.distance + previousPoint.width / 2 > prevEdgeLength + this.stairLength) {
          if (floor.userData.points[i].superstructure && floor.userData.points[i].superstructure.name === "stairs" &&
            floor.userData.points[i].distance < floor.userData.points[i].width / 2 + 1) {
            needRight = false;
          }
        }
      }
      if (nextPoint.superstructureType === "wall") {
        if (nextPoint.distance - nextPoint.width / 2 < -this.stairLength) {
          if (floor.userData.points[i].superstructure && floor.userData.points[i].superstructure.name === "stairs" &&
            floor.userData.points[i].distance + floor.userData.points[i].width / 2 > edgeLength - 1) {
            needLeft = false;
          }
        }
      }

      if (previousPoint.superstructure && previousPoint.superstructure.name === "stairs") {
        if (previousPoint.distance + previousPoint.width / 2 > prevEdgeLength - 1) {
          if (floor.userData.points[i].superstructureType === "wall" &&
            floor.userData.points[i].distance < floor.userData.points[i].width / 2 - this.stairLength) {
            needRight = false;
          }
        }
      }
      if (nextPoint.superstructure && nextPoint.superstructure.name === "stairs") {
        if (nextPoint.distance - nextPoint.width / 2 < 1) {
          if (floor.userData.points[i].superstructureType === "wall" &&
            floor.userData.points[i].distance + floor.userData.points[i].width / 2 > edgeLength + this.stairLength) {
            needLeft = false;
          }
        }
      }


      const vector = new THREE.Vector3(
        (edges[i].secondPoint.y - edges[i].firstPoint.y),
        0,
        -(edges[i].secondPoint.x - edges[i].firstPoint.x)
      );

      const edge = edges[i];
      const direction = new THREE.Vector3(edge.secondPoint.x - edge.firstPoint.x, 0, edge.secondPoint.y - edge.firstPoint.y);

      if (!floor.userData.points[i].superstructureType) {
        ///////////////////////////////////////////////////
        // no superstructure
        ///////////////////////////////////////////////////

        let isFirstPointTerminationPost = false;
        let isSecondPointTerminationPost = false;
        let firstPostPosition: any;
        let secondPostPosition: any;

        if (previousPoint.superstructureType) {
          if (this.toPrecision(previousPoint.distance + this.getAdditionalDistance(previousPoint) + previousPoint.width / 2) >= this.toPrecision(previousEdge.length - leftExtraOffset)) {
            isFirstPointTerminationPost = true;
            const secondVector = new THREE.Vector3(
              edges[i].secondPoint.x - edges[i].firstPoint.x,
              0,
              edges[i].secondPoint.y - edges[i].firstPoint.y
            );
            secondVector.setLength(Math.abs(isWood ? Offset + 1 : Offset));

            const firstVector = new THREE.Vector3(-secondVector.z, 0, secondVector.x);
            firstVector.setLength(Math.abs(Offset));

            if (Offset < 0) {
              firstVector.negate();
            }

            const terminationPostPosition = new THREE.Vector3(edges[i].firstPoint.x, deckHeight, edges[i].firstPoint.y);
            terminationPostPosition.add(firstVector);
            terminationPostPosition.add(secondVector);
            terminationPostPosition.vector = vector;
            terminationPostPosition.direction = direction;

            floor.userData.points[i].notCorner = true;

            if (previousPoint.superstructure && previousPoint.superstructure.name === "stairs") {
              terminationPostPosition.isStairPost = true;
              terminationPostPosition.isOutPoint = true;


              previousPoint.secondPostPosition = terminationPostPosition.clone();
              previousPoint.secondPostPosition.isStairPost = true;
              previousPoint.secondPostPosition.isOutPoint = true;
            }

            firstPostPosition = terminationPostPosition;
            terminationPostsPositions.push(terminationPostPosition);

            if (this.isTiersSuperstructureType(previousPoint)) {
              if (!previousPoint.terminationPosts) previousPoint.terminationPosts = {};
              previousPoint.terminationPosts.second = terminationPostPosition.clone();
            }
          }
        }

        if (nextPoint.superstructureType) {
          if (this.toPrecision(nextPoint.distance + this.getAdditionalDistance(nextPoint) - rightExtraOffset) <= this.toPrecision(nextPoint.width / 2)) {
            isSecondPointTerminationPost = true;
            const secondVector = new THREE.Vector3(
              edges[i].firstPoint.x - edges[i].secondPoint.x,
              0,
              edges[i].firstPoint.y - edges[i].secondPoint.y
            );
            secondVector.setLength(Math.abs(isWood ? Offset + 1 : Offset));

            const firstVector = new THREE.Vector3(secondVector.z, 0, -secondVector.x);
            firstVector.setLength(Math.abs(Offset));

            if (Offset < 0) {
              firstVector.negate();
            }

            const terminationPostPosition = new THREE.Vector3(edges[i].secondPoint.x, deckHeight, edges[i].secondPoint.y);
            terminationPostPosition.add(firstVector);
            terminationPostPosition.add(secondVector);
            terminationPostPosition.vector = vector;
            terminationPostPosition.direction = direction;
            nextPoint.notCorner = true;

            if (nextPoint.superstructure && nextPoint.superstructure.name === "stairs") {
              terminationPostPosition.isStairPost = true;
              terminationPostPosition.isOutPoint = true;


              nextPoint.firstPostPosition = terminationPostPosition.clone();
              nextPoint.firstPostPosition.isStairPost = true;
              nextPoint.firstPostPosition.isOutPoint = true;
              // nextPoint.firstPostPosition.needCorrection = true;
            }

            secondPostPosition = terminationPostPosition;
            terminationPostsPositions.push(terminationPostPosition);

            if (this.isTiersSuperstructureType(nextPoint)) {
              if (!nextPoint.terminationPosts) nextPoint.terminationPosts = {};
              nextPoint.terminationPosts.first = terminationPostPosition.clone();
            }
          }
        }

        const edge = {
          firstPoint: floor.userData.points[i],
          secondPoint: nextPoint,
          isFirstPointTerminationPost: isFirstPointTerminationPost,
          isSecondPointTerminationPost: isSecondPointTerminationPost,
          firstPostPosition: firstPostPosition,
          secondPostPosition: secondPostPosition
        };

        floor.userData.points[i].firstPostPosition = firstPostPosition;
        floor.userData.points[i].secondPostPosition = secondPostPosition;

        newEdges.push(edge);
      } else if (
        (this.toPrecision(floor.userData.points[i].distance + this.getAdditionalDistance(floor.userData.points[i]) - extraOffsetL) > this.toPrecision(floor.userData.points[i].width / 2)) &&
        (this.toPrecision(floor.userData.points[i].distance + this.getAdditionalDistance(floor.userData.points[i]) + floor.userData.points[i].width / 2) < this.toPrecision(edges[i].length - extraOffsetR))
      ) {
        ///////////////////////////////////////////////////
        // superstucture without touching another walls
        ///////////////////////////////////////////////////

        let isFirstPointTerminationPost = false;
        let isSecondPointTerminationPost = false;
        let firstPostPosition: any;
        let secondPostPosition: any;

        if (previousPoint.superstructureType) {
          if (this.toPrecision(previousPoint.distance + this.getAdditionalDistance(previousPoint) + previousPoint.width / 2) >= this.toPrecision(previousEdge.length - leftExtraOffset)) {
            isFirstPointTerminationPost = true;
            const secondVector = new THREE.Vector3(
              edges[i].secondPoint.x - edges[i].firstPoint.x,
              0,
              edges[i].secondPoint.y - edges[i].firstPoint.y
            );
            secondVector.setLength(Math.abs(isWood ? Offset + 1 : Offset));

            const firstVector = new THREE.Vector3(-secondVector.z, 0, secondVector.x);
            firstVector.setLength(Math.abs(Offset));

            if (Offset < 0) {
              firstVector.negate();
            }

            const terminationPostPosition = new THREE.Vector3(edges[i].firstPoint.x, deckHeight, edges[i].firstPoint.y);
            terminationPostPosition.add(firstVector);
            terminationPostPosition.add(secondVector);
            terminationPostPosition.vector = vector;
            terminationPostPosition.direction = direction;
            floor.userData.points[i].notCorner = true;

            if (previousPoint.superstructure && previousPoint.superstructure.name === "stairs") {
              terminationPostPosition.isStairPost = true;
              terminationPostPosition.isOutPoint = true;


              previousPoint.secondPostPosition = terminationPostPosition.clone();
              previousPoint.secondPostPosition.isStairPost = true;
              previousPoint.secondPostPosition.isOutPoint = true;

            }

            firstPostPosition = terminationPostPosition;
            terminationPostsPositions.push(terminationPostPosition);

            if (this.isTiersSuperstructureType(previousPoint)) {
              if (!previousPoint.terminationPosts) previousPoint.terminationPosts = {};
              previousPoint.terminationPosts.second = terminationPostPosition.clone();
            }
          }
        }

        if (nextPoint.superstructureType) {
          if (this.toPrecision(nextPoint.distance + this.getAdditionalDistance(nextPoint) - rightExtraOffset) <= this.toPrecision(nextPoint.width / 2)) {
            isSecondPointTerminationPost = true;
            const secondVector = new THREE.Vector3(
              edges[i].firstPoint.x - edges[i].secondPoint.x,
              0,
              edges[i].firstPoint.y - edges[i].secondPoint.y
            );
            secondVector.setLength(Math.abs(isWood ? Offset + 1 : Offset));

            const firstVector = new THREE.Vector3(secondVector.z, 0, -secondVector.x);
            firstVector.setLength(Math.abs(Offset));

            if (Offset < 0) {
              firstVector.negate();
            }

            const terminationPostPosition = new THREE.Vector3(edges[i].secondPoint.x, deckHeight, edges[i].secondPoint.y);
            terminationPostPosition.add(firstVector);
            terminationPostPosition.add(secondVector);
            terminationPostPosition.vector = vector;
            nextPoint.notCorner = true;
            terminationPostPosition.direction = direction;

            if (nextPoint.superstructure && nextPoint.superstructure.name === "stairs") {
              terminationPostPosition.isStairPost = true;
              terminationPostPosition.isOutPoint = true;

              nextPoint.firstPostPosition = terminationPostPosition.clone();
              nextPoint.firstPostPosition.isStairPost = true;
              nextPoint.firstPostPosition.isOutPoint = true;
            }

            secondPostPosition = terminationPostPosition;
            terminationPostsPositions.push(terminationPostPosition);

            if (this.isTiersSuperstructureType(nextPoint)) {
              if (!nextPoint.terminationPosts) nextPoint.terminationPosts = {};
              nextPoint.terminationPosts.first = terminationPostPosition.clone();
            }
          }
        }

        const centerPoint = new THREE.Vector3(edges[i].firstPoint.x, deckHeight, edges[i].firstPoint.y);
        const vect = new THREE.Vector3(
          edges[i].secondPoint.x - edges[i].firstPoint.x,
          0,
          edges[i].secondPoint.y - edges[i].firstPoint.y
        );
        vect.setLength(edges[i].firstPoint.distance + this.getAdditionalDistance(edges[i].firstPoint));
        centerPoint.add(vect);

        const offset = new THREE.Vector3(edges[i].firstPoint.x - edges[i].secondPoint.x, 0, edges[i].firstPoint.y - edges[i].secondPoint.y);
        offset.setLength(floor.userData.points[i].width / 2 - (isWood ? Offset + 1 : Offset));
        const normal = new THREE.Vector3(offset.z, 0, -offset.x);
        normal.setLength(Math.abs(Offset));

        if (Offset < 0) {
          normal.negate();
        }

        const leftPoint = centerPoint.clone();

        const shift = new THREE.Vector3(offset.x, offset.y, offset.z);
        shift.setLength(Math.abs(isWood ? Offset + 1 : Offset));

        if (Offset < 0) {
          shift.negate();
        }

        shift.negate();
        leftPoint.add(offset);
        offset.negate();
        const rightPoint = centerPoint.clone();
        rightPoint.add(offset);

        const lPoint = leftPoint.clone();
        const rPoint = rightPoint.clone();
        lPoint.add(normal);
        rPoint.add(normal);

        leftPoint.add(shift);
        shift.negate();
        rightPoint.add(shift);

        rPoint.vector = normal;
        lPoint.vector = normal;

        rPoint.direction = direction;
        lPoint.direction = direction;

        if (floor.userData.points[i].superstructure && floor.userData.points[i].superstructure.name === "stairs") {
          lPoint.isStairPost = true;
          rPoint.isStairPost = true;
        }

        const firstEdge = {
          firstPoint: floor.userData.points[i],
          secondPoint: new THREE.Vector2(leftPoint.x, leftPoint.z),
          isFirstPointTerminationPost: isFirstPointTerminationPost,
          isSecondPointTerminationPost: true,
          firstPostPosition: firstPostPosition,
          secondPostPosition: lPoint
        },
          secondEdge = {
            firstPoint: new THREE.Vector2(rightPoint.x, rightPoint.z),
            secondPoint: (i + 1) === floor.userData.points.length ? floor.userData.points[0] : floor.userData.points[i + 1],
            isFirstPointTerminationPost: true,
            isSecondPointTerminationPost: isSecondPointTerminationPost,
            firstPostPosition: rPoint,
            secondPostPosition: secondPostPosition
          };

        floor.userData.points[i].firstPostPosition = lPoint;
        floor.userData.points[i].secondPostPosition = rPoint;

        terminationPostsPositions.push(lPoint, rPoint);
        newEdges.push(firstEdge, secondEdge);

        if (this.isTiersSuperstructureType(edges[i].firstPoint)) {
          if (!edges[i].firstPoint.terminationPosts) edges[i].firstPoint.terminationPosts = {};
          edges[i].firstPoint.terminationPosts.first = lPoint.clone();
          edges[i].firstPoint.terminationPosts.second = rPoint.clone();
        }
      } else if (
        (this.toPrecision(floor.userData.points[i].distance + this.getAdditionalDistance(floor.userData.points[i]) - extraOffsetL) <= this.toPrecision(floor.userData.points[i].width / 2)) &&
        (this.toPrecision(floor.userData.points[i].distance + this.getAdditionalDistance(floor.userData.points[i]) + floor.userData.points[i].width / 2) < this.toPrecision(edges[i].length - extraOffsetR))
      ) {
        ///////////////////////////////////////////////////
        // superstucture without touching left wall
        ///////////////////////////////////////////////////

        let isFirstPointTerminationPost = false;
        let isSecondPointTerminationPost = false;
        let firstPostPosition: any;
        let secondPostPosition: any;

        if (previousPoint.superstructureType) {
          if (this.toPrecision(previousPoint.distance + this.getAdditionalDistance(previousPoint) + previousPoint.width / 2) >= this.toPrecision(previousEdge.length - leftExtraOffset)) {
            isFirstPointTerminationPost = true;
            floor.userData.points[i].notCorner = true;

            if (previousPoint.superstructureType !== 'wall' &&
              !(this.isTiersSuperstructureType(previousPoint) &&
                floor.userData.points[i].superstructureType === 'wall')) {
              const secondVector = new THREE.Vector3(
                edges[i].secondPoint.x - edges[i].firstPoint.x,
                0,
                edges[i].secondPoint.y - edges[i].firstPoint.y
              );
              secondVector.setLength(Math.abs(isWood ? Offset + 1 : Offset));

              const firstVector = new THREE.Vector3(-secondVector.z, 0, secondVector.x);
              firstVector.setLength(Math.abs(Offset));
              if (Offset < 0) {
                firstVector.negate();
              }

              const terminationPostPosition = new THREE.Vector3(
                edges[i].firstPoint.x,
                deckHeight,
                edges[i].firstPoint.y
              );
              terminationPostPosition.add(firstVector);
              terminationPostPosition.add(secondVector);
              terminationPostPosition.vector = vector;
              terminationPostPosition.direction = direction;

              if (previousPoint.superstructure && previousPoint.superstructure.name === "stairs") {
                terminationPostPosition.isStairPost = true;
                terminationPostPosition.isOutPoint = true;
                terminationPostPosition.connectedWithAnotherStair = true;


                previousPoint.secondPostPosition = terminationPostPosition.clone();
                previousPoint.secondPostPosition.isStairPost = true;
                previousPoint.secondPostPosition.isOutPoint = true;
                previousPoint.secondPostPosition.connectedWithAnotherStair = true;
              }
              if (needRight) {
                firstPostPosition = terminationPostPosition;
                terminationPostsPositions.push(terminationPostPosition);
              }
              if (this.isTiersSuperstructureType(previousPoint)) {
                if (!previousPoint.terminationPosts) previousPoint.terminationPosts = {};
                previousPoint.terminationPosts.second = terminationPostPosition.clone();
              }
            }
          }
        }

        if (nextPoint.superstructureType) {
          if (this.toPrecision(nextPoint.distance + this.getAdditionalDistance(nextPoint) - rightExtraOffset) <= this.toPrecision(nextPoint.width / 2)) {
            isSecondPointTerminationPost = true;
            const secondVector = new THREE.Vector3(
              edges[i].firstPoint.x - edges[i].secondPoint.x,
              0,
              edges[i].firstPoint.y - edges[i].secondPoint.y
            );
            secondVector.setLength(Math.abs(isWood ? Offset + 1 : Offset));

            const firstVector = new THREE.Vector3(secondVector.z, 0, -secondVector.x);
            firstVector.setLength(Math.abs(Offset));

            if (Offset < 0) {
              firstVector.negate();
            }

            const terminationPostPosition = new THREE.Vector3(
              edges[i].secondPoint.x,
              deckHeight,
              edges[i].secondPoint.y
            );
            terminationPostPosition.add(firstVector);
            terminationPostPosition.add(secondVector);
            terminationPostPosition.vector = vector;
            nextPoint.notCorner = true;
            terminationPostPosition.direction = direction;

            if (nextPoint.superstructure && nextPoint.superstructure.name === "stairs") {
              terminationPostPosition.isStairPost = true;
              terminationPostPosition.isOutPoint = true;

              nextPoint.firstPostPosition = terminationPostPosition.clone();
              nextPoint.firstPostPosition.isStairPost = true;
              nextPoint.firstPostPosition.isOutPoint = true;
            }

            if (needLeft) {
              secondPostPosition = terminationPostPosition;
              terminationPostsPositions.push(terminationPostPosition);
            }

            if (this.isTiersSuperstructureType(nextPoint)) {
              if (!nextPoint.terminationPosts) nextPoint.terminationPosts = {};
              nextPoint.terminationPosts.first = terminationPostPosition.clone();
            }
          }
        }

        const centerPoint = new THREE.Vector3(edges[i].firstPoint.x, deckHeight, edges[i].firstPoint.y);
        const vect = new THREE.Vector3(
          edges[i].secondPoint.x - edges[i].firstPoint.x,
          0,
          edges[i].secondPoint.y - edges[i].firstPoint.y
        );
        vect.setLength(edges[i].firstPoint.distance + this.getAdditionalDistance(edges[i].firstPoint));
        centerPoint.add(vect);

        const offset = new THREE.Vector3(
          edges[i].firstPoint.x - edges[i].secondPoint.x,
          0,
          edges[i].firstPoint.y - edges[i].secondPoint.y
        );
        offset.setLength(floor.userData.points[i].width / 2 - (isWood ? Offset + 1 : Offset));
        const normal = new THREE.Vector3(offset.z, 0, -offset.x);
        normal.setLength(Math.abs(Offset));

        if (Offset < 0) {
          normal.negate();
        }

        const leftPoint = centerPoint.clone();
        const shift = new THREE.Vector3(offset.x, offset.y, offset.z);
        shift.setLength(Math.abs(isWood ? Offset + 1 : Offset));

        if (Offset < 0) {
          shift.negate();
        }

        shift.negate();
        leftPoint.add(offset);
        offset.negate();
        const rightPoint = centerPoint.clone();
        rightPoint.add(offset);

        const lPoint = leftPoint.clone();
        const rPoint = rightPoint.clone();
        lPoint.add(normal);
        rPoint.add(normal);

        leftPoint.add(shift);
        shift.negate();
        rightPoint.add(shift);

        rPoint.vector = normal;
        lPoint.vector = normal;

        rPoint.direction = direction;


        if (floor.userData.points[i].superstructure && floor.userData.points[i].superstructure.name === "stairs") {
          lPoint.isStairPost = true;
          rPoint.isStairPost = true;
          lPoint.isOutPoint = true;
          if (isWood) {
            rPoint.needCorrection = true;
            floor.userData.points[i].superstructure.needCorrection = true;
          }
          lPoint.connectedWithAnotherStair = previousPoint.secondPostPosition ? previousPoint.secondPostPosition.connectedWithAnotherStair : false;
        }

        const secondEdge = {
          firstPoint: new THREE.Vector2(rightPoint.x, rightPoint.z),
          secondPoint: (i + 1) === floor.userData.points.length ? floor.userData.points[0] : floor.userData.points[i + 1],
          isFirstPointTerminationPost: true,
          isSecondPointTerminationPost: isSecondPointTerminationPost,
          firstPostPosition: rPoint,
          secondPostPosition: secondPostPosition
        };

        floor.userData.points[i].firstPostPosition = lPoint;
        floor.userData.points[i].secondPostPosition = rPoint;

        terminationPostsPositions.push(rPoint);
        newEdges.push(secondEdge);

        if (this.isTiersSuperstructureType(edges[i].firstPoint)) {
          if (!edges[i].firstPoint.terminationPosts) edges[i].firstPoint.terminationPosts = {};
          edges[i].firstPoint.terminationPosts.second = rPoint.clone();
        }
      } else if (
        (this.toPrecision(floor.userData.points[i].distance + this.getAdditionalDistance(floor.userData.points[i]) - extraOffsetL) > this.toPrecision(floor.userData.points[i].width / 2)) &&
        (this.toPrecision(floor.userData.points[i].distance + this.getAdditionalDistance(floor.userData.points[i]) + floor.userData.points[i].width / 2) >= this.toPrecision(edges[i].length - extraOffsetR))
      ) {
        ///////////////////////////////////////////////////
        // superstucture without touching right wall
        ///////////////////////////////////////////////////

        let isFirstPointTerminationPost = false;
        let isSecondPointTerminationPost = false;
        let firstPostPosition: any;
        let secondPostPosition: any;

        if (previousPoint.superstructureType) {
          if (this.toPrecision(previousPoint.distance + this.getAdditionalDistance(previousPoint) + previousPoint.width / 2) >= this.toPrecision(previousEdge.length - leftExtraOffset)) {
            isFirstPointTerminationPost = true;

            const secondVector = new THREE.Vector3(
              edges[i].secondPoint.x - edges[i].firstPoint.x,
              0,
              edges[i].secondPoint.y - edges[i].firstPoint.y
            );
            secondVector.setLength(Math.abs(isWood ? Offset + 1 : Offset));

            const firstVector = new THREE.Vector3(-secondVector.z, 0, secondVector.x);
            firstVector.setLength(Math.abs(Offset));

            if (Offset < 0) {
              firstVector.negate();
            }

            const terminationPostPosition = new THREE.Vector3(edges[i].firstPoint.x, deckHeight, edges[i].firstPoint.y);
            terminationPostPosition.add(firstVector);
            terminationPostPosition.add(secondVector);
            terminationPostPosition.vector = vector;
            terminationPostPosition.direction = direction;
            floor.userData.points[i].notCorner = true;

            if (previousPoint.superstructure && previousPoint.superstructure.name === "stairs") {
              terminationPostPosition.isStairPost = true;
              terminationPostPosition.isOutPoint = true;


              previousPoint.secondPostPosition = terminationPostPosition.clone();
              previousPoint.secondPostPosition.isStairPost = true;
              previousPoint.secondPostPosition.isOutPoint = true;
            }
            if (needRight) {
              firstPostPosition = terminationPostPosition;
              terminationPostsPositions.push(terminationPostPosition);
            }
            if (this.isTiersSuperstructureType(previousPoint)) {
              if (!previousPoint.terminationPosts) previousPoint.terminationPosts = {};
              previousPoint.terminationPosts.second = terminationPostPosition.clone();
            }
          }
        }

        if (nextPoint.superstructureType) {
          if (this.toPrecision(nextPoint.distance + this.getAdditionalDistance(nextPoint) - rightExtraOffset) <= this.toPrecision(nextPoint.width / 2)) {
            isSecondPointTerminationPost = true;
            nextPoint.notCorner = true;

            if (nextPoint.superstructureType !== 'wall' &&
              !(this.isTiersSuperstructureType(nextPoint) &&
                floor.userData.points[i].superstructureType === 'wall')) {
              const secondVector = new THREE.Vector3(
                edges[i].firstPoint.x - edges[i].secondPoint.x,
                0,
                edges[i].firstPoint.y - edges[i].secondPoint.y
              );
              secondVector.setLength(Math.abs(isWood ? Offset + 1 : Offset));

              const firstVector = new THREE.Vector3(secondVector.z, 0, -secondVector.x);
              firstVector.setLength(Math.abs(Offset));

              if (Offset < 0) {
                firstVector.negate();
              }

              const terminationPostPosition = new THREE.Vector3(
                edges[i].secondPoint.x,
                deckHeight,
                edges[i].secondPoint.y
              );
              terminationPostPosition.add(firstVector);
              terminationPostPosition.add(secondVector);
              terminationPostPosition.vector = vector;
              terminationPostPosition.direction = direction;

              if (nextPoint.superstructure && nextPoint.superstructure.name === "stairs") {
                terminationPostPosition.isStairPost = true;
                terminationPostPosition.isOutPoint = true;
                terminationPostPosition.connectedWithAnotherStair = true;

                nextPoint.firstPostPosition = terminationPostPosition.clone();
                nextPoint.firstPostPosition.isStairPost = true;
                nextPoint.firstPostPosition.isOutPoint = true;
                nextPoint.firstPostPosition.connectedWithAnotherStair = true;
              }
              if (needLeft) {
                secondPostPosition = terminationPostPosition;
                terminationPostsPositions.push(terminationPostPosition);
              }
              if (this.isTiersSuperstructureType(nextPoint)) {
                if (!nextPoint.terminationPosts) nextPoint.terminationPosts = {};
                nextPoint.terminationPosts.first = terminationPostPosition.clone();
              }
            }
          }
        }

        const centerPoint = new THREE.Vector3(edges[i].firstPoint.x, deckHeight, edges[i].firstPoint.y);
        const vect = new THREE.Vector3(
          edges[i].secondPoint.x - edges[i].firstPoint.x,
          0,
          edges[i].secondPoint.y - edges[i].firstPoint.y
        );
        vect.setLength(edges[i].firstPoint.distance + this.getAdditionalDistance(edges[i].firstPoint));
        centerPoint.add(vect);

        const offset = new THREE.Vector3(edges[i].firstPoint.x - edges[i].secondPoint.x, 0, edges[i].firstPoint.y - edges[i].secondPoint.y);
        offset.setLength(floor.userData.points[i].width / 2 - (isWood ? Offset + 1 : Offset));
        const normal = new THREE.Vector3(offset.z, 0, -offset.x);
        normal.setLength(Math.abs(Offset));

        if (Offset < 0) {
          normal.negate();
        }

        const leftPoint = centerPoint.clone();
        const shift = new THREE.Vector3(offset.x, offset.y, offset.z);
        shift.setLength(Math.abs(isWood ? Offset + 1 : Offset));

        if (Offset < 0) {
          shift.negate();
        }

        shift.negate();
        leftPoint.add(offset);
        offset.negate();
        const rightPoint = centerPoint.clone();
        rightPoint.add(offset);

        const lPoint = leftPoint.clone();
        const rPoint = rightPoint.clone();
        lPoint.add(normal);
        rPoint.add(normal);

        leftPoint.add(shift);
        shift.negate();
        rightPoint.add(shift);

        rPoint.vector = normal;
        lPoint.vector = normal;

        lPoint.direction = direction;

        if (floor.userData.points[i].superstructure && floor.userData.points[i].superstructure.name === "stairs") {
          lPoint.isStairPost = true;
          rPoint.isStairPost = true;
          rPoint.isOutPoint = true;
          rPoint.connectedWithAnotherStair = nextPoint.firstPostPosition ? nextPoint.firstPostPosition.connectedWithAnotherStair : false;
        }

        const firstEdge = {
          firstPoint: floor.userData.points[i],
          secondPoint: new THREE.Vector2(leftPoint.x, leftPoint.z),
          isFirstPointTerminationPost: isFirstPointTerminationPost,
          isSecondPointTerminationPost: true,
          firstPostPosition: firstPostPosition,
          secondPostPosition: lPoint
        };

        floor.userData.points[i].firstPostPosition = lPoint;
        floor.userData.points[i].secondPostPosition = rPoint;

        terminationPostsPositions.push(lPoint);
        newEdges.push(firstEdge);

        if (this.isTiersSuperstructureType(edges[i].firstPoint)) {
          if (!edges[i].firstPoint.terminationPosts) edges[i].firstPoint.terminationPosts = {};
          edges[i].firstPoint.terminationPosts.first = lPoint.clone();
        }
      } else if (
        (this.toPrecision(floor.userData.points[i].distance + this.getAdditionalDistance(floor.userData.points[i]) - extraOffsetL) <= this.toPrecision(floor.userData.points[i].width / 2)) &&
        (this.toPrecision(floor.userData.points[i].distance + this.getAdditionalDistance(floor.userData.points[i]) + floor.userData.points[i].width / 2) >= this.toPrecision(edges[i].length - extraOffsetR))
      ) {
        ///////////////////////////////////////////////////
        // superstucture touching both walls
        ///////////////////////////////////////////////////

        let isFirstPointTerminationPost = false;
        let isSecondPointTerminationPost = false;

        if (previousPoint.superstructureType) {
          if (this.toPrecision(previousPoint.distance + this.getAdditionalDistance(previousPoint) + previousPoint.width / 2) >= this.toPrecision(previousEdge.length - leftExtraOffset)) {
            isFirstPointTerminationPost = true;
            floor.userData.points[i].notCorner = true;

            if (previousPoint.superstructureType !== 'wall' &&
              !(this.isTiersSuperstructureType(previousPoint) &&
                floor.userData.points[i].superstructureType === 'wall')) {
              const secondVector = new THREE.Vector3(
                edges[i].secondPoint.x - edges[i].firstPoint.x,
                0,
                edges[i].secondPoint.y - edges[i].firstPoint.y
              );
              secondVector.setLength(Math.abs(isWood ? Offset + 1 : Offset));

              const firstVector = new THREE.Vector3(-secondVector.z, 0, secondVector.x);
              firstVector.setLength(Math.abs(Offset));

              if (Offset < 0) {
                firstVector.negate();
              }

              const terminationPostPosition = new THREE.Vector3(
                edges[i].firstPoint.x,
                deckHeight,
                edges[i].firstPoint.y
              );
              terminationPostPosition.add(firstVector);
              terminationPostPosition.add(secondVector);
              terminationPostPosition.vector = vector;
              terminationPostPosition.direction = direction;

              if (previousPoint.superstructure && previousPoint.superstructure.name === "stairs") {
                terminationPostPosition.isStairPost = true;
                terminationPostPosition.isOutPoint = true;
                terminationPostPosition.connectedWithAnotherStair = true;


                previousPoint.secondPostPosition = terminationPostPosition.clone();
                previousPoint.secondPostPosition.isStairPost = true;
                previousPoint.secondPostPosition.isOutPoint = true;
                previousPoint.secondPostPosition.connectedWithAnotherStair = true;
              }

              if (needRight) {
                terminationPostsPositions.push(terminationPostPosition);
                floor.userData.points[i].firstPostPosition = terminationPostPosition;
              }

              if (this.isTiersSuperstructureType(previousPoint)) {
                if (!previousPoint.terminationPosts) previousPoint.terminationPosts = {};
                previousPoint.terminationPosts.second = terminationPostPosition.clone();
              }
            }
          }
        }

        if (nextPoint.superstructureType) {
          if (this.toPrecision(nextPoint.distance + this.getAdditionalDistance(nextPoint) - rightExtraOffset) <= this.toPrecision(nextPoint.width / 2)) {
            isSecondPointTerminationPost = true;
            nextPoint.notCorner = true;

            if (nextPoint.superstructureType !== 'wall' &&
              !(this.isTiersSuperstructureType(nextPoint) &&
                floor.userData.points[i].superstructureType === 'wall')) {
              const secondVector = new THREE.Vector3(
                edges[i].firstPoint.x - edges[i].secondPoint.x,
                0,
                edges[i].firstPoint.y - edges[i].secondPoint.y
              );
              secondVector.setLength(Math.abs(isWood ? Offset + 1 : Offset));

              const firstVector = new THREE.Vector3(secondVector.z, 0, -secondVector.x);
              firstVector.setLength(Math.abs(Offset));

              if (Offset < 0) {
                firstVector.negate();
              }

              const terminationPostPosition = new THREE.Vector3(
                edges[i].secondPoint.x,
                deckHeight,
                edges[i].secondPoint.y
              );
              terminationPostPosition.add(firstVector);
              terminationPostPosition.add(secondVector);
              terminationPostPosition.vector = vector;
              terminationPostPosition.direction = direction;

              if (nextPoint.superstructure && nextPoint.superstructure.name === "stairs") {
                terminationPostPosition.isStairPost = true;
                terminationPostPosition.isOutPoint = true;
                terminationPostPosition.connectedWithAnotherStair = true;

                nextPoint.firstPostPosition = terminationPostPosition.clone();
                nextPoint.firstPostPosition.isStairPost = true;
                nextPoint.firstPostPosition.isOutPoint = true;
                nextPoint.firstPostPosition.connectedWithAnotherStair = true;
              }

              if (needLeft) {
                terminationPostsPositions.push(terminationPostPosition);
                floor.userData.points[i].secondPostPosition = terminationPostPosition;
              }

              if (this.isTiersSuperstructureType(nextPoint)) {
                if (!nextPoint.terminationPosts) nextPoint.terminationPosts = {};
                nextPoint.terminationPosts.first = terminationPostPosition.clone();
              }
            }
          }
        }
      }
    }

    return { newEdges, terminationPostsPositions };
  }

  // mirrors applyRailRunLimits: the edge starting at points[i] currently has
  // a railing run unless its anchor hasn't been clicked (noRail) or its run
  // was dragged below one 60" section; an unlimited edge always counts
  private edgeHasRailRun(points, i): boolean {
    const point = points[i];
    if (!point) {
      return true;
    }
    if (point.noRail) {
      return false;
    }
    if (!point.railStart && !point.railStop) {
      return true;
    }
    const next = points[(i + 1) % points.length];
    const length = Math.sqrt(
      Math.pow(next.x - point.x, 2) + Math.pow(next.y - point.y, 2)
    );
    return length - (point.railStart || 0) - (point.railStop || 0) >= this.sizeHelper.INCHToCM(60);
  }

  getPositionsForCornerPosts(floor, cornerPost, insideCornerPost, intermediatePost, cornerPost45degree, cornerPost135degree) {
    const cornerPoints = [];
    const rotations = [];
    const deckHeight = floor.getWorldPosition(new THREE.Vector3()).y;
    const points = floor.userData.points;
    const edges = Edges.fromPoints(points);

    for (let i = 0; i < points.length; i++) {
      const point = new THREE.Vector3(points[i].x, deckHeight, points[i].y);
      const intermediatePostPosition = point.clone();

      const A = points[i],
        B = points[(i + 1) % points.length],
        C = points[i === 0 ? points.length - 1 : i - 1];
      const firstWallVector = new THREE.Vector3(B.x - A.x, 0, B.y - A.y);
      const secondWallVector = new THREE.Vector3(C.x - A.x, 0, C.y - A.y);

      const orient = firstWallVector.x * secondWallVector.z - firstWallVector.z * secondWallVector.x;

      let non45 = false;
      let is45 = false;
      let is135 = false;
      if (firstWallVector.angleTo(secondWallVector) < (3 * Math.PI / 4 - 0.005) ||
        firstWallVector.angleTo(secondWallVector) > (3 * Math.PI / 4 + 0.005)) {
        non45 = true;
      }

      const norm = new THREE.Vector3(0, 0, 1);
      let n: any;
      if (+cornerPost.userData.entity.offset >= 0) {
        n = cornerPost.userData.entity ?
          this.sizeHelper.INCHToCM(2.5) + +cornerPost.userData.entity.offset * Math.cos(Math.PI / 4) :
          16;

        firstWallVector.setLength(n);
        secondWallVector.setLength(n);

        if (orient >= 0) {
          var inside = false;
          if (!non45 && cornerPost45degree) {
            is45 = true;
          }

          var angle = firstWallVector.angleTo(norm);
          if (firstWallVector.x < 0) {
            // norm.negate();
            angle = -firstWallVector.angleTo(norm);
          }

        } else {
          if (cornerPost.userData.entity.cornerType === 'Double') {
            n = 3.7;
          } else {
            if (cornerPost.userData.entity.railingType.indexOf('Composite') !== -1) {
              // correct post position for composite railing type
              n = 12.7;
            } else {
              n = 7.7;
            }
          }

          var inside = false;

          if (!non45 && cornerPost135degree) {
            is135 = true;
          } else {
            inside = true;
          }

          var angle = firstWallVector.angleTo(norm) + Math.PI / 2;

          if (firstWallVector.x < 0) {
            // norm.negate();
            angle = -firstWallVector.angleTo(norm) + Math.PI / 2;
          }

          firstWallVector.negate();
          secondWallVector.negate();
        }

      } else {
        n = cornerPost.userData.entity ?
          this.sizeHelper.INCHToCM(2.5) + +cornerPost.userData.entity.offset * Math.cos(Math.PI / 4) :
          16;

        if (orient >= 0) {

          var inside = false;

          if (!non45 && cornerPost45degree) {
            is45 = true;
          }

          var angle = firstWallVector.angleTo(norm);

          if (firstWallVector.x < 0) {
            // norm.negate();
            angle = -firstWallVector.angleTo(norm);
          }


        } else {
          n = insideCornerPost.userData.entity ? -this.sizeHelper.INCHToCM(2.5) + +insideCornerPost.userData.entity.offset * Math.cos(Math.PI / 4) : 7;

          var inside = false;
          if (!non45 && cornerPost135degree && intermediatePost.userData.entity.mountType !== 'Fascia Bracket Mount') {
            is135 = true;
          } else {
            inside = true;
          }

          var angle = firstWallVector.angleTo(norm) + Math.PI / 2;

          if (firstWallVector.x < 0) {
            // norm.negate();
            angle = -firstWallVector.angleTo(norm) + Math.PI / 2;
          }
        }

        if (cornerPost.userData.entity.mountType === 'Fascia Bracket Mount') {
          angle -= Math.PI / 2;
        }
      }

      let non90 = false;
      let scalarM = firstWallVector.x * secondWallVector.x + firstWallVector.z * secondWallVector.z;
      firstWallVector.setLength(n);
      secondWallVector.setLength(n);

      if (scalarM < -0.05 || scalarM > 0.05) {
        non90 = true;
      }

      if (non90) {
        if (!is45 && !is135) {
          const postSize = new THREE.Box3().setFromObject(intermediatePost).getSize(new THREE.Vector3());
          const edge = edges.elementAt(i);
          const prev = edges.prev(edge);
          const edgeOffset = +intermediatePost.userData.entity.offset;
          const heightOffset = new THREE.Vector3(0, deckHeight, 0);
          let isInsideAngle = (edge.direction.x * prev.direction.negate().z - edge.direction.z * prev.direction.negate().x) < 0;
          let positionForCurrentEdge, positionForPrevEdge;
          let offsetTowardDirection, offsetTowardNormal;
          const isInside = edgeOffset > 0;
          let rotationCorrection = 0;

          if ((isInside && !isInsideAngle)) { // || (!isInside && isInsideAngle)) {
            const angleBetweenEdges = prev.direction.negate().angleTo(edge.direction);
            offsetTowardDirection = ((postSize.x + edgeOffset) / Math.tan(angleBetweenEdges / 2)) + postSize.x / 2;
            offsetTowardNormal = postSize.z / 2 + edgeOffset;
          } else if (!isInside && isInsideAngle) {
            const angleBetweenEdges = prev.direction.negate().angleTo(edge.direction);
            offsetTowardDirection = ((postSize.x - edgeOffset) / Math.tan(angleBetweenEdges / 2)) + postSize.x / 2;
            offsetTowardNormal = postSize.z / 2 + edgeOffset;
            rotationCorrection = THREE.Math.degToRad(180);

          } else {

            offsetTowardDirection = postSize.x / 2;
            if (intermediatePost.userData.entity.mountType === "Fascia Mount") {
              const angleBetweenEdges = prev.direction.negate().angleTo(edge.direction);
              offsetTowardDirection += this.sizeHelper.INCHToCM(1.5) / Math.sin(angleBetweenEdges / 2);
            }
            offsetTowardNormal = this.sizeHelper.INCHToCM(2.5) + edgeOffset;
            rotationCorrection = THREE.Math.degToRad(180);
          }

          positionForCurrentEdge =
            edge.direction.multiplyScalar(offsetTowardDirection)
              .add(edge.normal.multiplyScalar(offsetTowardNormal))
              .add(edge.start)
              .add(heightOffset);
          positionForPrevEdge =
            prev.direction.negate().multiplyScalar(offsetTowardDirection)
              .add(prev.normal.multiplyScalar(offsetTowardNormal))
              .add(edge.start)
              .add(heightOffset);

          positionForCurrentEdge.floorPoint = { x: edge.start.x, y: edge.start.z };
          positionForCurrentEdge.first = true;
          positionForPrevEdge.floorPoint = { x: edge.start.x, y: edge.start.z };
          positionForPrevEdge.second = true;
          positionForPrevEdge.non90 = positionForCurrentEdge.non90 = true;
          positionForPrevEdge.inside = positionForCurrentEdge.inside = isInside;
          // each of the two posts belongs to one adjacent side — skip the
          // post whose side has no railing run yet (dock anchors / run limits)
          if (this.edgeHasRailRun(points, i === 0 ? points.length - 1 : i - 1)) {
            cornerPoints.push(positionForPrevEdge);
            rotations.push(prev.rotationAngle + rotationCorrection);
          }
          if (this.edgeHasRailRun(points, i)) {
            cornerPoints.push(positionForCurrentEdge);
            rotations.push(edge.rotationAngle + rotationCorrection);
          }

        } else {
          if (is45) {

            rotations.push(angle - Math.PI / 4);
          } else {
            rotations.push(angle);
          }

          firstWallVector.setLength(n);
          secondWallVector.setLength(n);

          point.inside = inside;
          point.is45 = is45;
          point.is135 = is135;
          point.floorPoint = points[i];

          point.add(firstWallVector);
          point.add(secondWallVector);

          cornerPoints.push(point);

        }

      } else {
        rotations.push(angle);

        firstWallVector.setLength(n);
        secondWallVector.setLength(n);

        point.inside = inside;

        point.add(firstWallVector);
        point.add(secondWallVector);

        const Offset = this.sizeHelper.INCHToCM(2.5) + +intermediatePost.userData.entity.offset;

        const firstShift = firstWallVector.clone();
        const secondShift = secondWallVector.clone();

        firstShift.setLength(Offset);
        secondShift.setLength(Offset);

        if (Offset < 0 && inside) {
          firstShift.negate();
          secondShift.negate();
        }

        intermediatePostPosition.add(firstShift);
        intermediatePostPosition.add(secondShift);

        point.intermediatePostPosition = intermediatePostPosition;
        point.floorPoint = points[i];
        point.previousFloorPoint = points[(i > 0) ? (i - 1) : (floor.userData.points.length - 1)];
        point.nextFloorPoint = points[(i < floor.userData.points.length - 1) ? (i + 1) : 0];

        cornerPoints.push(point);
      }

      if (points[i].notCorner) {
        // match by THIS corner's coordinates — the non-90 branch may have
        // pushed nothing for it (sides without railing), and the previously
        // used "last pushed entry" then belonged to another corner and wrongly
        // hid that corner's post
        cornerPoints.filter(cp => {
          return cp && cp.floorPoint &&
            cp.floorPoint.x === points[i].x && cp.floorPoint.y === points[i].y;
        })
          .forEach(cp => {
            if (cp) cp.notCorner = true;
          });
      }
    }
    return { rotations, cornerPoints }
  }

}



// WEBPACK FOOTER //
// ./src/app/services/railing/services/railing-calculation-helper.service.ts